import { motion, MotionValue, useSpring, useTransform } from "framer-motion"
import React from "react"
import styled from "styled-components"

type VideoPlayToggleProps = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>
}

const VideoPlayToggleButton = styled.button`
  cursor: pointer;
  background: none;
  outline: none;
  border: none;

  transition: transform 100ms ease-in-out;

  &:hover svg :last-child {
    transform: scale(1.1);
    transform-origin: center;
  }
`

export const VideoPlayToggle = ({ videoRef }: VideoPlayToggleProps) => {
  const [isPlaying, setIsPlaying] = React.useState(true)
  const [reverse, setReverse] = React.useState(true)

  const progress: MotionValue<number> = useSpring(0, { bounce: 0 })
  const strokeDashoffset = useTransform(progress, [0, 1], reverse ? [-10, 0] : [0, 10])

  const video = videoRef.current

  const toggleIsPlaying = async () => {
    setIsPlaying(false)

    if (video?.paused) {
      await video?.play()
    } else {
      video?.pause()
    }
  }

  React.useEffect(() => {
    const listener = () => {
      setIsPlaying((isPlaying) => {
        if (isPlaying) {
          progress.jump(0)
          setReverse((reverse) => !reverse)
        }
        return true
      })
    }
    video?.addEventListener("playing", listener)
    return () => video?.removeEventListener("playing", listener)
  }, [videoRef.current])

  React.useEffect(() => {
    const timeUpdateListener = () => {
      const video = videoRef.current!
      const next = video.currentTime / video.duration
      progress.set(isNaN(next) ? 0 : next)
    }
    video?.addEventListener("timeupdate", timeUpdateListener)
    return () => video?.removeEventListener("timeupdate", timeUpdateListener)
  }, [videoRef.current])

  return (
    <VideoPlayToggleButton onClick={() => toggleIsPlaying()}>
      <motion.svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <motion.circle
          cx="32"
          cy="32"
          r="30"
          stroke="#ffffffbc"
          strokeWidth="4"
          pathLength={10}
          strokeDasharray={10}
          style={{ strokeDashoffset }}
        />
        {isPlaying ? (
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M27 21.5C27 20.1193 25.8807 19 24.5 19C23.1193 19 22 20.1193 22 21.5V42.5C22 43.8807 23.1193 45 24.5 45C25.8807 45 27 43.8807 27 42.5L27 21.5ZM42 21.5C42 20.1193 40.8807 19 39.5 19C38.1193 19 37 20.1193 37 21.5L37 42.5C37 43.8807 38.1193 45 39.5 45C40.8807 45 42 43.8807 42 42.5L42 21.5Z"
            fill="#ffffffbc"
          />
        ) : (
          <path
            d="M45.3125 30.3297C46.7292 31.1476 46.7292 33.1924 45.3125 34.0103L26.1875 45.0521C24.7708 45.87 23 44.8476 23 43.2118L23 21.1282C23 19.4923 24.7708 18.47 26.1875 19.2879L45.3125 30.3297Z"
            fill="#ffffffbc"
          />
        )}
      </motion.svg>
    </VideoPlayToggleButton>
  )
}
